import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Shared 2-or-3-step progress pill row for the booking journey
 * (Booking -> Checkout -> Thankyou). Only shown when the event actually
 * requires an online deposit/payment via PayZone — pay-in-full-on-site /
 * bank-transfer bookings skip the "Payment" step entirely, since there's
 * nothing to pay online.
 *
 *   <BookingSteps active="details" hasDeposit={advance > 0} />
 *   <BookingSteps active="payment" hasDeposit={needsDeposit} />
 *   <BookingSteps active="confirmation" hasDeposit={needsDeposit} />
 *
 * `active` is one of 'details' | 'payment' | 'confirmation'. The Booking and
 * Checkout pages both render as step 1 ("Details") — checkout is the
 * sign-in/confirm step, not a distinct step of its own.
 */
export default function BookingSteps({ active, hasDeposit }) {
  const { t } = useTranslation();

  if (!hasDeposit) {
    return (
      <ol className="mt-5 flex items-center gap-2 text-xs font-semibold text-ink-subtle">
        <StepPill active={active === 'details'}>1. {t('booking.stepDetails')}</StepPill>
        <StepConnector />
        <StepPill active={active === 'confirmation'}>2. {t('booking.stepDone')}</StepPill>
      </ol>
    );
  }

  return (
    <ol className="mt-5 flex items-center gap-2 text-xs font-semibold text-ink-subtle">
      <StepPill active={active === 'details'}>1. {t('booking.stepDetails')}</StepPill>
      <StepConnector />
      <StepPill active={active === 'payment'}>2. {t('booking.stepCheckout')}</StepPill>
      <StepConnector />
      <StepPill active={active === 'confirmation'}>3. {t('booking.stepDone')}</StepPill>
    </ol>
  );
}

function StepPill({ active, children }) {
  return (
    <li
      className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 ${
        active ? 'bg-brand-500 text-white' : 'bg-white text-ink-subtle ring-1 ring-slate-200'
      }`}
    >
      {children}
    </li>
  );
}

function StepConnector() {
  return <span className="h-px w-4 shrink-0 bg-slate-200" aria-hidden />;
}
